import React from 'react';
import { Box, LinearProgress, Skeleton, Tooltip, Typography } from '@mui/material';

import { PromptDriftItem } from '../../../../store/modules/merchant/types';
import { DRIFT_LEVEL_COLOR, DriftStatusChip } from './DriftStatusChip';
import {
  DRIFT_LEVEL_LABEL,
  DRIFT_STABLE_MAX,
  DRIFT_WATCH_MAX,
  DriftLevel,
  formatCount,
  formatDriftScore,
  getDriftLevel,
} from './formatMetrics';

type PromptDriftPanelProps = {
  items?: PromptDriftItem[];
  isLoading?: boolean;
};

/** Full scale of the per-version bar; the "high drift" threshold sits at its midpoint. */
const BAR_FULL_SCALE = DRIFT_WATCH_MAX * 2;

const NO_DATA_HINT = 'No Task topic data yet for this prompt version';

const GRID_COLUMNS = 'minmax(0, 2fr) 72px minmax(150px, 1fr)';

const LEGEND: { level: DriftLevel; range: string }[] = [
  { level: 'stable', range: `< ${DRIFT_STABLE_MAX * 100}%` },
  { level: 'watch', range: `${DRIFT_STABLE_MAX * 100}–${DRIFT_WATCH_MAX * 100}%` },
  { level: 'high', range: `> ${DRIFT_WATCH_MAX * 100}%` },
];

/** Session-weighted average across versions that have a drift score. */
const getWeightedScore = (items: PromptDriftItem[]): number | null => {
  const scored = items.filter((item) => item.driftScore !== null);
  if (scored.length === 0) {
    return null;
  }
  const totalSessions = scored.reduce((sum, item) => sum + item.sessionCount, 0);
  if (totalSessions <= 0) {
    return scored[0].driftScore;
  }
  return (
    scored.reduce((sum, item) => sum + (item.driftScore ?? 0) * item.sessionCount, 0) /
    totalSessions
  );
};

export const PromptDriftPanel = ({ items = [], isLoading }: PromptDriftPanelProps) => {
  const weightedScore = getWeightedScore(items);
  const headlineLevel = getDriftLevel(weightedScore);
  const scoredCount = items.filter((item) => item.driftScore !== null).length;

  const subheadline = (() => {
    if (items.length === 0) {
      return null;
    }
    if (scoredCount === 0) {
      return NO_DATA_HINT;
    }
    const suffix = scoredCount === items.length ? '' : ` of ${items.length}`;
    return `Session-weighted across ${scoredCount}${suffix} prompt version${
      scoredCount === 1 ? '' : 's'
    }`;
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'secondary.main',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        minHeight: 136,
      }}
      data-testid="ella-prompt-drift-panel"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Typography
          variant="medium"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', lineHeight: '18px' }}
        >
          Prompt Drift
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width="20%" height={36} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              variant="huge"
              fontWeight="semibold"
              sx={{ lineHeight: '36px', letterSpacing: '-1.5px' }}
              data-testid="ella-prompt-drift-headline"
            >
              {formatDriftScore(weightedScore)}
            </Typography>
            <DriftStatusChip level={headlineLevel} size="medium" />
          </Box>
        )}

        {!isLoading && subheadline && (
          <Typography variant="small" color="text.secondary">
            {subheadline}
          </Typography>
        )}

        {!isLoading && items.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                gap: 1,
                pb: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="small" color="text.secondary">
                Version
              </Typography>
              <Typography variant="small" color="text.secondary">
                Sessions
              </Typography>
              <Typography variant="small" color="text.secondary" sx={{ textAlign: 'right' }}>
                Drift
              </Typography>
            </Box>
            {items.map((item) => {
              const level = getDriftLevel(item.driftScore);
              const hasScore = level !== 'unknown';
              return (
                <Box
                  key={item.promptVersion}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: GRID_COLUMNS,
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="small" sx={{ wordBreak: 'break-word' }}>
                      {item.promptVersion}
                    </Typography>
                    <Typography variant="small" color="text.secondary">
                      {formatCount(item.sessionCount)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                      }}
                    >
                      <Tooltip title={hasScore ? '' : NO_DATA_HINT}>
                        <Typography
                          variant="small"
                          color={hasScore ? 'text.primary' : 'text.secondary'}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {hasScore ? formatDriftScore(item.driftScore) : 'N/A'}
                        </Typography>
                      </Tooltip>
                      <DriftStatusChip level={level} />
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      hasScore ? Math.min(100, ((item.driftScore ?? 0) / BAR_FULL_SCALE) * 100) : 0
                    }
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      backgroundColor: 'secondary.light',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: DRIFT_LEVEL_COLOR[level].text,
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}

        {!isLoading && items.length === 0 && (
          <Typography variant="medium" color="text.secondary">
            No prompt drift data for this range
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="small" color="text.secondary" sx={{ lineHeight: '16px' }}>
          Drift from original prompt intent
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {LEGEND.map(({ level, range }) => (
            <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: DRIFT_LEVEL_COLOR[level].text,
                }}
              />
              <Typography variant="small" color="text.secondary" sx={{ lineHeight: '16px' }}>
                {DRIFT_LEVEL_LABEL[level]} {range}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
